import { useMediaQuery } from '@vueuse/core'

/**
 * Reactive breakpoint detection for responsive design
 *
 * Provides reactive boolean values for the three main breakpoints defined in the design system.
 * Matches the breakpoints defined in tokens.css:
 * - Small (mobile): < 600px
 * - Medium (tablet): >= 600px and < 1024px
 * - Large (desktop): >= 1024px
 *
 * @example Basic usage
 * ```typescript
 * const { isSmallScreen, isMediumScreen, isLargeScreen } = useBreakpoints()
 * ```
 *
 * @example Conditional rendering based on screen size
 * ```vue
 * <script setup>
 * const { isSmallScreen, isMediumScreen, isLargeScreen } = useBreakpoints()
 * </script>
 *
 * <template>
 *   <div v-if="isSmallScreen">Mobile View</div>
 *   <div v-else-if="isMediumScreen">Tablet View</div>
 *   <div v-else-if="isLargeScreen">Desktop View</div>
 * </template>
 * ```
 *
 * @example Combining with computed properties
 * ```vue
 * <script setup>
 * const { isSmallScreen } = useBreakpoints()
 * const columnCount = computed(() => isSmallScreen.value ? 1 : 3)
 * </script>
 * ```
 *
 * @returns Object with reactive boolean refs for each breakpoint
 * @see {@link packages/ui-vue/src/composables/useBreakpoints.ts:1}
 */

/**
 * Design system breakpoint values (in pixels)
 * - Small: < 600px (mobile)
 * - Medium: 600-1023px (tablet)
 * - Large: >= 1024px (desktop)
 */
export const BREAKPOINT_SMALL = 600
export const BREAKPOINT_MEDIUM = 1024

export function useBreakpoints() {
  const isSmall = useMediaQuery(`(max-width: ${BREAKPOINT_SMALL - 1}px)`)
  const isMedium = useMediaQuery(`(min-width: ${BREAKPOINT_SMALL}px) and (max-width: ${BREAKPOINT_MEDIUM - 1}px)`)
  const isLarge = useMediaQuery(`(min-width: ${BREAKPOINT_MEDIUM}px)`)

  return {
    /**
     * True when viewport width is < 600px (mobile)
     */
    isSmallScreen: isSmall,

    /**
     * True when viewport width is >= 600px and < 1024px (tablet)
     */
    isMediumScreen: isMedium,

    /**
     * True when viewport width is >= 1024px (desktop)
     */
    isLargeScreen: isLarge,
  }
}
